import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  MessageSquarePlus,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toInfoSynthesisSessionPath } from "@/core/constants";
import { cn } from "@/lib/utils";
import type { SynthesisSessionSummary } from "../../domain/entities/synthesis-session";

type SessionSidebarProps = {
  expanded: boolean;
  onToggleExpanded: () => void;
  listLoading: boolean;
  items: SynthesisSessionSummary[];
  currentSessionId?: string;
  onNewChat: () => void;
  onRequestDelete: (s: SynthesisSessionSummary) => void;
};

export const SessionSidebar = ({
  expanded,
  onToggleExpanded,
  listLoading,
  items,
  currentSessionId,
  onNewChat,
  onRequestDelete,
}: SessionSidebarProps) => {
  if (!expanded) {
    return (
      <aside
        className="flex w-12 shrink-0 flex-col border-r border-border bg-muted/20"
        aria-label="Conversations (collapsed)"
      >
        <div className="flex flex-col gap-1 border-b border-border p-1.5">
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="size-9 shrink-0 cursor-pointer"
            aria-label="Expand conversations"
            onClick={onToggleExpanded}
          >
            <ChevronRight className="size-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="size-9 shrink-0 cursor-pointer"
            aria-label="New conversation"
            onClick={onNewChat}
          >
            <MessageSquarePlus className="size-4" />
          </Button>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="flex w-72 shrink-0 flex-col border-r border-border bg-muted/20"
      aria-label="Conversations"
    >
      <div className="flex items-center gap-1 border-b border-border p-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-9 shrink-0 cursor-pointer"
          aria-label="Collapse conversations"
          onClick={onToggleExpanded}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="min-w-0 flex-1 justify-start gap-2 cursor-pointer"
          onClick={onNewChat}
        >
          <MessageSquarePlus className="size-4 shrink-0" />
          <span className="truncate">New conversation</span>
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {listLoading ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">
            No conversations yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {items.map((s) => {
              const active = s.id === currentSessionId;
              return (
                <li
                  key={s.id}
                  className={cn(
                    "group flex items-center gap-0.5 rounded-md",
                    active && "bg-primary/10",
                  )}
                >
                  <Link
                    to={toInfoSynthesisSessionPath(s.id)}
                    className={cn(
                      "min-w-0 flex-1 truncate rounded-md px-2 py-2 text-left text-sm transition-colors",
                      active
                        ? "font-medium text-foreground"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                    )}
                  >
                    {s.title}
                  </Link>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0 opacity-0 group-hover:opacity-100"
                    aria-label="Delete"
                    onClick={() => onRequestDelete(s)}
                  >
                    <Trash2 className="size-3.5 text-muted-foreground" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
};
